import React, { useCallback, useEffect, useState } from "react";
import MainLayoutWithoutFooter from "../components/MainLayoutWithoutFooter";
import OfflineOrderDetails from "../components/modals/OfflineOrderDetails";
import { getOfflineOrders } from "../utils/androidOfflineCart";

/**
 * Safely convert a value to a number.
 */
const toNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
};

/**
 * Return the first available value that is not null or undefined.
 */
const firstDefined = (...values) =>
  values.find((value) => value !== null && value !== undefined);

/**
 * Convert different order type formats into a consistent numeric value.
 *
 * Supported examples:
 * 1, "1", "takeaway", "take-away"
 * 2, "2", "delivery"
 * 3, "3", "dinein", "dine-in"
 */
const normalizeOrderType = (order) => {
  const value = firstDefined(
    order?.ordertype,
    order?.orderType,
    order?.order_type
  );

  if (value === null || value === undefined || value === "") {
    return null;
  }

  const normalized = String(value).trim().toLowerCase();

  if (["1", "takeaway", "take-away", "take away"].includes(normalized)) {
    return 1;
  }

  if (["2", "delivery"].includes(normalized)) {
    return 2;
  }

  if (["3", "dinein", "dine-in", "dine in"].includes(normalized)) {
    return 3;
  }

  const numericValue = Number(normalized);

  return Number.isFinite(numericValue) ? numericValue : null;
};

/**
 * Normalize offline order status.
 *
 * Numeric status codes are preserved as numbers.
 * Text statuses remain text instead of becoming zero.
 */
const normalizeOrderStatus = (order) => {
  const value = firstDefined(
    order?.orderstatus,
    order?.orderStatus,
    order?.status
  );

  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return numericValue;
  }

  return String(value).trim();
};

/**
 * Normalize payment status.
 */
const normalizePaidStatus = (order) => {
  const value = firstDefined(
    order?.paid,
    order?.isPaid,
    order?.is_paid,
    order?.paymentStatus,
    order?.payment_status,
    order?.status
  );

  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (["true", "yes", "paid"].includes(normalized)) {
      return 1;
    }

    if (
      [
        "false",
        "no",
        "unpaid",
        "open",
        "pending",
        "",
      ].includes(normalized)
    ) {
      return 0;
    }
  }

  return toNumber(value, 0);
};

/**
 * Format order creation date.
 */
const formatShortDate = (date) => {
  if (!date) {
    return "--";
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "--";
  }

  return (
    parsedDate.toLocaleDateString("en-GB") +
    " " +
    parsedDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })
  );
};

/**
 * Get a readable order status.
 *
 * Supports numeric codes and text statuses.
 */
const getOfflineOrderStatusLabel = (status) => {
  const normalized = String(status ?? "").trim().toLowerCase();

  const statusLabels = {
    "0": "N/A",
    "1": "N/A",
    "2": "N/A",
    "3": "Placed",
    "4": "Cooking",
    "5": "Ready for delivery",
    "6": "Out for delivery",
    "7": "Completed",

    placed: "Placed",
    pending: "Pending",
    cooking: "Cooking",
    preparing: "Cooking",
    ready: "Ready",
    "ready for delivery": "Ready for delivery",
    "out for delivery": "Out for delivery",
    completed: "Completed",
    complete: "Completed",
    cancelled: "Cancelled",
    canceled: "Cancelled",
    failed: "Failed",
  };

  return statusLabels[normalized] ??
    (normalized
      ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
      : "N/A");
};

/**
 * Get a readable order type.
 *
 * Does not access order.tables.length, so missing table data
 * cannot crash the page.
 */
const getOfflineOrderTypeLabel = (order) => {
  const orderType = normalizeOrderType(order);

  const source = String(order?.source ?? "pos")
    .trim()
    .toLowerCase();

  let orderSource = "";

  if (source === "pos") {
    if (orderType === 1 || orderType === 2) {
      orderSource = "Phone Order";
    } else if (orderType === 3) {
      orderSource = "Dine In";
    } else {
      orderSource = "POS Order";
    }
  } else if (source === "table_order") {
    orderSource = "Table Order";
  } else if (
    order?.uber_order_id ||
    order?.uberOrderId ||
    String(order?.tender ?? "").toLowerCase() === "ubereats" ||
    source === "uber"
  ) {
    orderSource = "Uber";
  } else if (source === "website" || source === "ai") {
    orderSource = "Website";
  } else {
    orderSource = source
      ? source.charAt(0).toUpperCase() + source.slice(1)
      : "Unknown";
  }

  let orderTypeLabel = "N/A";

  if (orderType === 1) {
    orderTypeLabel = "Take Away";
  } else if (orderType === 2) {
    orderTypeLabel = "Delivery";
  } else if (orderType === 3) {
    orderTypeLabel = "Dine In";
  }

  const tables = Array.isArray(order?.tables) ? order.tables : [];

  const tableNames = tables
    .map((table) => {
      if (typeof table === "string" || typeof table === "number") {
        return String(table);
      }

      return table?.name ?? table?.tableName ?? "";
    })
    .filter(Boolean);

  const singleTableName = firstDefined(
    order?.table_name,
    order?.tableName
  );

  if (singleTableName) {
    tableNames.push(String(singleTableName));
  }

  const tableLabel = [
    ...new Set(tableNames),
  ].join(", ");

  return (
    <>
      {orderSource}
      <br />

      <small className="text-uppercase text-muted">
        {orderType === 3
          ? tableLabel || (order?.table_id != null ||
              order?.tableId != null
              ? `Table ${order.table_id ?? order.tableId}`
              : "Dine In")
          : orderTypeLabel}
      </small>
    </>
  );
};

/**
 * Get payment status.
 */
const getOfflineOrderPaidLabel = (order) => {
  const paid = normalizePaidStatus(order);

  if (paid !== 1) {
    return "Unpaid";
  }

  const paymode = toNumber(
    firstDefined(order?.paymode, order?.payMode),
    0
  );

  let paymentMode = "";

  switch (paymode) {
    case 1:
      paymentMode = " (CASH)";
      break;

    case 2:
      paymentMode = " (EFTPOS)";
      break;

    case 3:
      paymentMode = " (ONLINE)";
      break;

    default:
      paymentMode = "";
  }

  return `Paid${paymentMode}`;
};

/**
 * Get the server's name.
 */
const getOfflineServerName = (order) => {
  const server = order?.server ?? order?.waiter ?? null;

  if (typeof server === "string" && server.trim()) {
    return server;
  }

  if (!server && order?.serverName) {
    return order.serverName;
  }

  const firstName =
    server?.first_name ??
    server?.firstName ??
    order?.server_first_name ??
    "";

  const lastName =
    server?.last_name ??
    server?.lastName ??
    order?.server_last_name ??
    "";

  const fullName = [firstName, lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || "N/A";
};

/**
 * Get a readable order source.
 */
const getOfflineOrderSourceLabel = (order) => {
  const source = String(order?.source ?? "pos")
    .trim()
    .toLowerCase();

  const sourceLabels = {
    pos: "POS",
    uber: "Uber",
    website: "Website",
    ai: "Website",
    table_order: "NFC Table Order",
  };

  return sourceLabels[source] ?? "--";
};

/**
 * Normalize offline orders.
 *
 * Every order stays independent.
 * Orders are never grouped by table ID or tableSessionId.
 */
const normalizeOfflineOrders = (result) => {
  const localOrders = Array.isArray(result)
    ? result
    : Array.isArray(result?.orders)
    ? result.orders
    : Array.isArray(result?.data)
    ? result.data
    : [];

  return localOrders.map((order, index) => {
    const localOrderId = firstDefined(
      order?.localOrderId,
      order?.local_order_id,
      order?.id,
      order?.orderid,
      order?.orderNumber,
      `offline-order-${index}`
    );

    const ordertype = normalizeOrderType(order);

    const customerName =
      order?.customer_name ??
      order?.customerName ??
      [
        order?.orderUser?.firstName,
        order?.orderUser?.lastName,
      ]
        .filter(Boolean)
        .join(" ") ??
      "";

    const items = Array.isArray(order?.items)
      ? order.items
      : Array.isArray(order?.orderItems)
      ? order.orderItems
      : [];

    return {
      ...order,

      // Use a stable local identifier for React and modal selection.
      id: String(localOrderId),
      localOrderId: String(localOrderId),

      orderid:
        order?.orderid ??
        order?.orderNumber ??
        order?.backendOrderId ??
        localOrderId,

      ordertype,

      source: order?.source ?? "pos",

      customer_name: customerName,

      server_id:
        order?.server_id ??
        order?.serverId ??
        null,

      table_id:
        order?.table_id ??
        order?.tableId ??
        null,

      tableSessionId:
        order?.tableSessionId ??
        order?.table_session_id ??
        null,

      orderstatus: normalizeOrderStatus(order),

      paid: normalizePaidStatus(order),

      paymode: toNumber(
        firstDefined(order?.paymode, order?.payMode),
        0
      ),

      FinalAmount: toNumber(
        firstDefined(
          order?.FinalAmount,
          order?.finalAmount,
          order?.total,
          order?.grandTotal
        ),
        0
      ),

      total: toNumber(order?.total, 0),

      subtotal: toNumber(order?.subtotal, 0),

      tax: toNumber(order?.tax, 0),

      discount: toNumber(order?.discount, 0),

      created_at:
        order?.created_at ??
        order?.createdAt ??
        order?.created_at_local ??
        null,

      items,

      // Always provide a safe array for code that needs table information.
      tables: Array.isArray(order?.tables)
        ? order.tables
        : [],
    };
  });
};

const isDineInOrder = (order) => {
  const value = firstDefined(
    order?.ordertype,
    order?.orderType,
    order?.order_type
  );

  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  return ["3", "dinein", "dine-in", "dine in"].includes(
    normalized
  );
};

const groupOfflineOrders = (orders) => {
  const groups = new Map();

  orders.forEach((order) => {
    const tableSessionId =
      order?.tableSessionId ??
      order?.table_session_id ??
      null;

    // Only group dine-in orders that have a valid session ID.
    const shouldGroup =
      isDineInOrder(order) &&
      tableSessionId != null &&
      String(tableSessionId).trim() !== "";

    const groupKey = shouldGroup
      ? `dinein-session:${String(tableSessionId)}`
      : `order:${order.id}`;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        ...order,
        id: groupKey,
        groupKey,
        isSessionGroup: shouldGroup,
        sessionOrders: [],
        items: [],
        subtotal: 0,
        tax: 0,
        discount: 0,
        total: 0,
        FinalAmount: 0,
      });
    }

    const group = groups.get(groupKey);

    group.sessionOrders.push(order);

    group.items.push(
      ...(Array.isArray(order.items) ? order.items : [])
    );

    group.subtotal += Number(order.subtotal ?? 0);
    group.tax += Number(order.tax ?? 0);
    group.discount += Number(order.discount ?? 0);

    const orderTotal = Number(
      order.FinalAmount ??
      order.finalAmount ??
      order.total ??
      0
    );

    group.total += orderTotal;
    group.FinalAmount += orderTotal;

    // A session is paid only when all its orders are paid.
    const isPaid =
      Number(order.paid) === 1 ||
      order.paid === true;

    if (group.sessionOrders.length === 1) {
      group.paid = isPaid ? 1 : 0;
    } else {
      group.paid =
        group.paid === 1 && isPaid ? 1 : 0;
    }
  });

  return Array.from(groups.values()).sort((a, b) => {
    const dateA = Number(a.created_at ?? a.createdAt ?? 0);
    const dateB = Number(b.created_at ?? b.createdAt ?? 0);

    return dateB - dateA;
  });
};

const OfflineOrders = () => {
  const [orders, setOrders] = useState([]);
  const [displayOrders, setDisplayOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderId, setOrderId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  /**
   * Fetch offline orders.
   */
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const result = await getOfflineOrders();

          console.log(
            "[OFFLINE_ORDERS] Raw result:",
            JSON.stringify(result, null, 2)
          );

      const mappedOrders = normalizeOfflineOrders(result);

      console.log(
        "[OFFLINE_ORDERS] Normalized orders:",
         JSON.stringify(mappedOrders, null, 2)
      );

      setOrders(mappedOrders);
      setDisplayOrders(groupOfflineOrders(mappedOrders));
    } catch (error) {
      console.error(
        "[OFFLINE_ORDERS] Failed to fetch offline orders:",
        error
      );

      setOrders([]);
      setErrorMessage(
        error?.message || "Failed to load offline orders."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  /**
   * Render one independent order row.
   */
  const renderOrderRow = (order) => {
    const customerName =
      order?.customer_name ||
      [
        order?.orderUser?.firstName,
        order?.orderUser?.lastName,
      ]
        .filter(Boolean)
        .join(" ") ||
      "--";

    const contactNumber =
      order?.orderUser?.contactNumber ??
      order?.customer_phone ??
      order?.customerPhone ??
      "";

    return (
      <tr key={order.id}>
        <td>{order.orderid ?? "--"}</td>

        <td>
          {customerName}

          {contactNumber && (
            <div className="text-muted small">
              {contactNumber}
            </div>
          )}
        </td>

        <td>
          {getOfflineOrderStatusLabel(order.orderstatus)}
        </td>

        <td>
          {getOfflineOrderTypeLabel(order)}

          <div className="text-muted small">
            {getOfflineOrderSourceLabel(order)}
          </div>
        </td>

        <td>
          {getOfflineServerName(order)}
        </td>

        <td>
          {getOfflineOrderPaidLabel(order)}
        </td>

        <td>
          {formatShortDate(order.created_at)}
        </td>

        <td>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#orderDetailModal"
            onClick={() => setOrderId(order.id)}
          >
            Details
          </button>
        </td>
      </tr>
    );
  };

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent orderPage pb-3">
        <div className="container">
          <div className="sbRow gap-2 mb-3">
            <h2 className="lgTitle mb-0">
              Offline Orders
            </h2>

            <button
              type="button"
              className="btn btnPrimary ms-auto"
              onClick={fetchOrders}
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>

          <div className="cardStyle">
            <div className="table-responsive">
              <table className="table tableStyle2 table-borderless align-middle">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Server</th>
                    <th>Paid</th>
                    <th>Date Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="text-center py-4"
                      >
                        Loading offline orders...
                      </td>
                    </tr>
                  ) : errorMessage ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="text-center py-4 text-danger"
                      >
                        {errorMessage}

                        <div className="mt-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={fetchOrders}
                          >
                            Try Again
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="text-center py-4"
                      >
                        No offline orders found.
                      </td>
                    </tr>
                  ) : (
                    displayOrders.map((order) => renderOrderRow(order))
                  )}
                </tbody>
              </table>
            </div>
          </div>

         <OfflineOrderDetails
  orderId={orderId}
  orders={orders}
    displayOrders={displayOrders}
/>
        </div>
      </div>
    </MainLayoutWithoutFooter>
  );
};
export default OfflineOrders;